/**
 * Simulation History Cache Service
 *
 * Caches simulation results in IndexedDB for persistent access across sessions.
 * Allows users to review past simulations without re-running them.
 */

import type { SimulationOutput, SimulationInput } from './simulationStore';
import type { OutputVariable, TimeSeries, Environment } from './parsers/types';

const DB_NAME = 'envelop-simulation-history';
const DB_VERSION = 1;
const HISTORY_STORE = 'simulation-history';
const MAX_HISTORY_ENTRIES = 20;

let db: IDBDatabase | null = null;

/**
 * Simulation history entry stored in IndexedDB.
 */
export interface SimulationHistoryEntry {
  /** Unique identifier */
  id: string;
  /** Timestamp when simulation was run */
  timestamp: Date;
  /** Input file information */
  input: {
    idfName: string;
    epwName?: string | undefined;
  };
  /** Simulation duration in milliseconds */
  duration: number;
  /** Whether simulation completed successfully */
  success: boolean;
  /** Error message if failed */
  error?: string | undefined;
  /** Summary statistics */
  summary: {
    /** Number of output variables */
    variableCount: number;
    /** Number of severe errors */
    severeErrors?: number | undefined;
    /** Number of warnings */
    warnings?: number | undefined;
  };
  /** Full output data (optional, can be large) */
  output?: SimulationOutput | undefined;
  /** Available output variables */
  availableVariables?: OutputVariable[] | undefined;
}

/**
 * Serialized history entry for IndexedDB storage.
 * Maps are converted to arrays for JSON serialization.
 */
interface SerializedHistoryEntry {
  id: string;
  timestamp: Date;
  input: {
    idfName: string;
    epwName?: string | undefined;
  };
  duration: number;
  success: boolean;
  error?: string | undefined;
  summary: {
    variableCount: number;
    severeErrors?: number | undefined;
    warnings?: number | undefined;
  };
  availableVariables?: OutputVariable[] | undefined;
  output?: {
    eso?: {
      programVersion: string;
      environments: Environment[];
      variables: [number, OutputVariable][];
      timeSeries: [number, TimeSeries][];
    };
    mtr?: {
      programVersion: string;
      environments: Environment[];
      variables: [number, OutputVariable][];
      timeSeries: [number, TimeSeries][];
    };
    err?: SimulationOutput['err'];
    html?: string;
    csv?: string;
  };
}

/**
 * Open the IndexedDB database.
 */
async function openDatabase(): Promise<IDBDatabase> {
  if (db) return db;

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      reject(new Error('Failed to open simulation history database'));
    };

    request.onsuccess = () => {
      db = request.result;
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = (event.target as IDBOpenDBRequest).result;

      if (!database.objectStoreNames.contains(HISTORY_STORE)) {
        const store = database.createObjectStore(HISTORY_STORE, { keyPath: 'id' });
        store.createIndex('timestamp', 'timestamp', { unique: false });
        store.createIndex('idfName', 'input.idfName', { unique: false });
      }
    };
  });
}

/**
 * Generate a unique ID for a history entry.
 */
function generateId(): string {
  return `sim-${String(Date.now())}-${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Serialize a history entry for IndexedDB storage.
 * Converts Map objects to arrays for JSON compatibility.
 */
function serializeEntry(entry: SimulationHistoryEntry): SerializedHistoryEntry {
  const serialized: SerializedHistoryEntry = {
    id: entry.id,
    timestamp: entry.timestamp,
    input: { idfName: entry.input.idfName },
    duration: entry.duration,
    success: entry.success,
    summary: { variableCount: entry.summary.variableCount },
  };

  // Handle optional properties by conditionally adding them
  if (entry.input.epwName !== undefined) {
    serialized.input.epwName = entry.input.epwName;
  }
  if (entry.error !== undefined) {
    serialized.error = entry.error;
  }
  if (entry.summary.severeErrors !== undefined) {
    serialized.summary.severeErrors = entry.summary.severeErrors;
  }
  if (entry.summary.warnings !== undefined) {
    serialized.summary.warnings = entry.summary.warnings;
  }
  if (entry.availableVariables !== undefined) {
    serialized.availableVariables = entry.availableVariables;
  }

  if (entry.output) {
    serialized.output = {};

    if (entry.output.err) {
      serialized.output.err = entry.output.err;
    }
    if (entry.output.html) {
      serialized.output.html = entry.output.html;
    }
    if (entry.output.csv) {
      serialized.output.csv = entry.output.csv;
    }

    if (entry.output.eso) {
      serialized.output.eso = {
        programVersion: entry.output.eso.programVersion,
        environments: entry.output.eso.environments,
        variables: Array.from(entry.output.eso.variables.entries()),
        timeSeries: Array.from(entry.output.eso.timeSeries.entries()),
      };
    }

    if (entry.output.mtr) {
      serialized.output.mtr = {
        programVersion: entry.output.mtr.programVersion,
        environments: entry.output.mtr.environments,
        variables: Array.from(entry.output.mtr.variables.entries()),
        timeSeries: Array.from(entry.output.mtr.timeSeries.entries()),
      };
    }
  }

  return serialized;
}

/**
 * Deserialize a history entry from IndexedDB storage.
 * Converts arrays back to Map objects.
 */
function deserializeEntry(serialized: SerializedHistoryEntry): SimulationHistoryEntry {
  const entry: SimulationHistoryEntry = {
    id: serialized.id,
    timestamp: new Date(serialized.timestamp),
    input: { idfName: serialized.input.idfName },
    duration: serialized.duration,
    success: serialized.success,
    summary: { variableCount: serialized.summary.variableCount },
  };

  // Handle optional properties by conditionally adding them
  if (serialized.input.epwName !== undefined) {
    entry.input.epwName = serialized.input.epwName;
  }
  if (serialized.error !== undefined) {
    entry.error = serialized.error;
  }
  if (serialized.summary.severeErrors !== undefined) {
    entry.summary.severeErrors = serialized.summary.severeErrors;
  }
  if (serialized.summary.warnings !== undefined) {
    entry.summary.warnings = serialized.summary.warnings;
  }
  if (serialized.availableVariables !== undefined) {
    entry.availableVariables = serialized.availableVariables;
  }

  if (serialized.output) {
    entry.output = {};

    if (serialized.output.err) {
      entry.output.err = serialized.output.err;
    }
    if (serialized.output.html) {
      entry.output.html = serialized.output.html;
    }
    if (serialized.output.csv) {
      entry.output.csv = serialized.output.csv;
    }

    if (serialized.output.eso) {
      entry.output.eso = {
        programVersion: serialized.output.eso.programVersion,
        environments: serialized.output.eso.environments,
        variables: new Map(serialized.output.eso.variables),
        timeSeries: new Map(serialized.output.eso.timeSeries),
      };
    }

    if (serialized.output.mtr) {
      entry.output.mtr = {
        programVersion: serialized.output.mtr.programVersion,
        environments: serialized.output.mtr.environments,
        variables: new Map(serialized.output.mtr.variables),
        timeSeries: new Map(serialized.output.mtr.timeSeries),
      };
    }
  }

  return entry;
}

/**
 * Save a simulation to history.
 */
export async function saveSimulationToHistory(
  input: SimulationInput,
  output: SimulationOutput | null,
  availableVariables: OutputVariable[],
  duration: number,
  error?: string
): Promise<string> {
  const database = await openDatabase();

  const entry: SimulationHistoryEntry = {
    id: generateId(),
    timestamp: new Date(),
    input: { idfName: input.idfName },
    duration,
    success: !error && output !== null,
    summary: {
      variableCount: availableVariables.length,
    },
    availableVariables,
  };

  // Handle optional properties
  if (input.epwName !== undefined) {
    entry.input.epwName = input.epwName;
  }
  if (error !== undefined) {
    entry.error = error;
  }
  if (output?.err?.severeCount !== undefined) {
    entry.summary.severeErrors = output.err.severeCount;
  }
  if (output?.err?.warningCount !== undefined) {
    entry.summary.warnings = output.err.warningCount;
  }
  if (output !== null) {
    entry.output = output;
  }

  const serialized = serializeEntry(entry);

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(HISTORY_STORE, 'readwrite');
    const store = transaction.objectStore(HISTORY_STORE);
    const request = store.put(serialized);

    request.onsuccess = async () => {
      // Enforce max history entries
      await enforceHistoryLimit();
      resolve(entry.id);
    };

    request.onerror = () => {
      reject(new Error('Failed to save simulation to history'));
    };
  });
}

/**
 * Get all simulation history entries (metadata only, no full output).
 */
export async function getSimulationHistory(): Promise<SimulationHistoryEntry[]> {
  const database = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(HISTORY_STORE, 'readonly');
    const store = transaction.objectStore(HISTORY_STORE);
    const index = store.index('timestamp');
    const request = index.getAll();

    request.onsuccess = () => {
      const entries = (request.result as SerializedHistoryEntry[]).map((e) => {
        // Return without full output for list view
        const entry = deserializeEntry(e);
        delete entry.output;
        return entry;
      });
      // Sort by most recent first
      entries.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
      resolve(entries);
    };

    request.onerror = () => {
      reject(new Error('Failed to get simulation history'));
    };
  });
}

/**
 * Get a specific simulation history entry with full output data.
 */
export async function getSimulationHistoryEntry(
  id: string
): Promise<SimulationHistoryEntry | null> {
  const database = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(HISTORY_STORE, 'readonly');
    const store = transaction.objectStore(HISTORY_STORE);
    const request = store.get(id);

    request.onsuccess = () => {
      const result = request.result as SerializedHistoryEntry | undefined;
      if (result) {
        resolve(deserializeEntry(result));
      } else {
        resolve(null);
      }
    };

    request.onerror = () => {
      reject(new Error('Failed to get simulation history entry'));
    };
  });
}

/**
 * Delete a simulation history entry.
 */
export async function deleteSimulationHistoryEntry(id: string): Promise<void> {
  const database = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(HISTORY_STORE, 'readwrite');
    const store = transaction.objectStore(HISTORY_STORE);
    const request = store.delete(id);

    request.onsuccess = () => { resolve(); };
    request.onerror = () => { reject(new Error('Failed to delete simulation history entry')); };
  });
}

/**
 * Clear all simulation history.
 */
export async function clearSimulationHistory(): Promise<void> {
  const database = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(HISTORY_STORE, 'readwrite');
    const store = transaction.objectStore(HISTORY_STORE);
    const request = store.clear();

    request.onsuccess = () => { resolve(); };
    request.onerror = () => { reject(new Error('Failed to clear simulation history')); };
  });
}

/**
 * Enforce maximum history entries limit.
 */
async function enforceHistoryLimit(): Promise<void> {
  const entries = await getSimulationHistory();

  if (entries.length <= MAX_HISTORY_ENTRIES) return;

  // Remove oldest entries
  const toRemove = entries.slice(MAX_HISTORY_ENTRIES);
  for (const entry of toRemove) {
    await deleteSimulationHistoryEntry(entry.id);
  }
}

/**
 * Get simulation history statistics.
 */
export async function getSimulationHistoryStats(): Promise<{
  totalSimulations: number;
  successfulSimulations: number;
  failedSimulations: number;
  totalDuration: number;
  averageDuration: number;
}> {
  const entries = await getSimulationHistory();

  const successful = entries.filter((e) => e.success).length;
  const failed = entries.filter((e) => !e.success).length;
  const totalDuration = entries.reduce((sum, e) => sum + e.duration, 0);

  return {
    totalSimulations: entries.length,
    successfulSimulations: successful,
    failedSimulations: failed,
    totalDuration,
    averageDuration: entries.length > 0 ? totalDuration / entries.length : 0,
  };
}
